import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { NurseResponse } from "@vTypes/nurse-type";
import httpClient from "@/config/axios";
import type * as type from "@vTypes";
import { toast } from "@/components/toast/CustomToast";

interface InvokeAndSavePreferenceParams {
    wantedInput: string;
    fullNurseSchema: NurseResponse[];
    activeDate: { year: number; month: number };
    groupId?: string;
}

interface Warning {
    message: string;
    excluded_items: {
        day: number;
        shift: string;
        date: string;
    }[];
    existing_off_count: number;
    excluded_count: number;
    limit: number;
}

interface InvokeResponse {
    response: type.PreferenceData & {
        warning?: Warning;
        aide_status?: {
            code?: string;
            ok?: boolean;
        } | null;
    };
}

function ensureAideInvokeOk(data: InvokeResponse) {
    if (data?.response?.aide_status?.ok === false) {
        const code = data.response.aide_status.code ?? "unknown";
        throw new Error(`AIDE invoke failed (${code})`);
    }
}

interface SaveCalendarDataParams {
    fullNurseSchema: NurseResponse[];
    targetData: Record<string, { type: string; comment: string | null }>;
    activeDate: { year: number; month: number };
    isReset: boolean;
    preferenceData: type.PreferenceData | null;
    groupId?: string;
}

interface FinalSubmitParams {
    year: number;
    month: number;
    groupId?: string;
}

interface RetractSubmissionParams {
    year: number;
    month: number;
    groupId?: string;
}

// 에이드 근무 선호도 생성? 및 저장 훅
export const useInvokeAndSavePreference = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ wantedInput, fullNurseSchema, activeDate }: InvokeAndSavePreferenceParams) => {
            const payload = {
                request: wantedInput,
                schema: fullNurseSchema,
                case: null,
                year: activeDate.year,
                month: activeDate.month,
            };

            const data = await httpClient.post<InvokeResponse>(
                `/wanted/invoke`,
                payload
            );
            ensureAideInvokeOk(data);

            const preferencePayload = {
                year: activeDate.year,
                month: activeDate.month,
                data: { ...data.response, request: wantedInput },
            };

            await httpClient.post(
                `/preferences`,
                preferencePayload
            );
            return data;
        },
        onSuccess: (_, variables) => {
            const { year, month } = variables.activeDate;
            const { groupId } = variables;
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.latest(year, month, groupId) });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.all(year, month, groupId) });
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
            });
        },
        onError: () => {
            toast.error("AIDE 요청 처리에 실패했습니다. 다시 시도해주세요.");
        },
    });
};

// 최종 제출 훅
export const useFinalSubmit = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ year, month, groupId }: FinalSubmitParams) => {
            const params: { year: number; month: number; group_id?: string } = { year, month };
            if (groupId) params.group_id = groupId;

            const latestData = await httpClient.get<any>(`/preferences/latest`, { params });

            let endpoint = "/preferences/submit";
            const body = { year, month };

            if (!latestData.preference_data) {
                endpoint = "/preferences/submit/empty";
            }

            await httpClient.post(endpoint, body);
        },
        onSuccess: (_, variables) => {
            const { year, month, groupId } = variables;
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.preferences.schedule(year, month, groupId),
            });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.all(year, month, groupId) });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.latest(year, month, groupId) });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wanted.all(groupId) });
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
            });
            // 알림 리스트 캐싱 무효화 (팝오버, 모달 모두)
            queryClient.invalidateQueries({ queryKey: ["alarms"] });
        },
    });
};

// 캘린더 데이터 저장 훅
export const useSaveCalendarData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ fullNurseSchema, targetData, activeDate, isReset, preferenceData }: SaveCalendarDataParams) => {
            const cases = [];
            for (const [dateStr, data] of Object.entries(targetData)) {
                cases.push({
                    date: dateStr,
                    shift: data.type,
                    comment: data.comment,
                });
            }

            const invokePayload = {
                request: "",
                schema: fullNurseSchema,
                case: cases,
                year: activeDate.year,
                month: activeDate.month,
                preference: isReset ? [] : preferenceData?.preference,
            };

            const data = await httpClient.post<InvokeResponse>(
                `/wanted/invoke`,
                invokePayload
            );
            ensureAideInvokeOk(data);

            const preferencePayload = {
                year: activeDate.year,
                month: activeDate.month,
                ...data.response,
                request: "",
                preference: isReset
                    ? []
                    : (preferenceData?.preference ?? data.response.preference),
            };

            await httpClient.post(`/preferences`, preferencePayload);
            return data;
        },
        onSuccess: (_, variables) => {
            const { year, month } = variables.activeDate;
            const { groupId } = variables;
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.latest(year, month, groupId) });
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
            });
        },
        onError: () => {
            toast.error("AIDE 요청 처리에 실패했습니다. 다시 시도해주세요.");
        },
    });
};

// 제출 철회 훅
export const useRetractSubmission = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ year, month }: RetractSubmissionParams) => {
            await httpClient.post(`/preferences/retract`, { year, month });
        },
        onSuccess: (_, variables) => {
            const { year, month, groupId } = variables;
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.all(year, month, groupId) });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.preferences.latest(year, month, groupId) });
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
            });
        },
    });
};
